/* eslint-disable no-nested-ternary */
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import format from 'date-fns/format';

import {
  UploadIcon,
  CheckedIcon,
  WarningIcon,
  TrashBinIcon,
} from 'modules/ui/assets';
import { RegularSm } from 'modules/ui/typography';

import InputLabel from '../InputLabel';
import Button from '../Button';
import Spinner from '../Spinner';

import {
  Container,
  UploadedFileContainer,
  FileMetadataContainer,
  FileActionsContainer,
  IconButton,
} from './styledComponents';
import { Colors } from 'modules/ui/theme/colors';

export type ValidationStatus = 'idle' | 'validating' | 'valid' | 'error';

type Props = {
  onChange: (files: File) => void;
  label: string;
  file: File | null;
  accept?: string;
  validationStatus?: ValidationStatus;
  validationError?: string;
  onRemove?: () => void;
};

const FileStatusIcon: React.FC<{ validationStatus: ValidationStatus }> = ({
  validationStatus,
}) => {
  switch (validationStatus) {
    case 'validating':
      return <Spinner />;
    case 'error':
      return <WarningIcon width={32} height={32} fill={Colors.Destructive} />;
    case 'valid':
      return <CheckedIcon width={32} height={32} />;
    default:
      return <CheckedIcon width={32} height={32} />;
  }
};

const DragAndDrop: React.FC<Props> = ({
  onChange,
  label,
  file,
  accept,
  validationStatus = 'idle',
  validationError,
  onRemove,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onChange(acceptedFiles && acceptedFiles[0]);
    },
    [onChange],
  );
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
  });

  const replaceHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => {
      if (e) {
        e.stopPropagation();
      }
      open();
    },
    [open],
  );

  const removeHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      onRemove?.();
    },
    [onRemove],
  );

  return (
    <>
      <InputLabel>{label}</InputLabel>
      <Container {...getRootProps()}>
        <input
          {...getInputProps()}
          accept={accept || '.csv, .pol, .tcs, .xls, .xsls, .xml, .text'}
        />
        {isDragActive ? (
          <p>Drop the files here ...</p>
        ) : !file ? (
          <>
            <UploadIcon fill="#0E91EF" />
            <p>Drag & drop your file here, or choose a file</p>
          </>
        ) : (
          <UploadedFileContainer>
            <FileStatusIcon validationStatus={validationStatus} />
            <FileMetadataContainer>
              <RegularSm>{`Filename: ${file.name}`}</RegularSm>
              {validationStatus === 'error' && validationError ? (
                <RegularSm color={Colors.Destructive}>
                  {validationError}
                </RegularSm>
              ) : (
                <RegularSm>
                  {`Date added: ${format(
                    new Date(file.lastModified),
                    'EEEE, MMM dd, yyyy',
                  )}`}
                </RegularSm>
              )}
            </FileMetadataContainer>
            <FileActionsContainer>
              <Button
                isSecondary
                label="Replace"
                clickHandler={replaceHandler}
              />
              {onRemove && (
                <IconButton
                  type="button"
                  onClick={removeHandler}
                  aria-label="Remove file"
                >
                  <TrashBinIcon />
                </IconButton>
              )}
            </FileActionsContainer>
          </UploadedFileContainer>
        )}
      </Container>
    </>
  );
};

export default React.memo(DragAndDrop);
